/**
 * Desktop selected-filters row with a Show More / Show Less toggle that
 * expands or collapses the chip list.
 *
 * This component orchestrates a fair amount of animation, measurement, and
 * layout timing. See `SelectedFiltersPanel.md` (same folder) for the full
 * explanation of the timelines, why width snaps on expand vs. transitions
 * on collapse, why max-height is snapped to scrollHeight at collapse click,
 * etc. Inline comments here are intentionally brief — they point back to
 * the doc.
 */
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from 'react';

import styled, { css, keyframes } from 'styled-components';

import CloseIcon2 from '~/icons/components/CloseIcon2/CloseIcon2';
import HideIcon from '~/icons/components/HideIcon';
import PlusIcon2 from '~/icons/components/PlusIcon2';
import FormattedMessage from '~/techstyle-shared/react-intl/src/FormattedMessage/FormattedMessage';

import {
  SELECTED_FILTERS_BUTTON_FADE_OUT_MS,
  SELECTED_FILTERS_CHIP_ENTER_DELAY_MS,
  SELECTED_FILTERS_CHIP_ENTER_DURATION_MS,
  SELECTED_FILTERS_CHIP_ENTER_EASING,
  SELECTED_FILTERS_CHIP_ENTER_STAGGER_MS,
  SELECTED_FILTERS_CHIP_EXIT_DURATION_MS,
  SELECTED_FILTERS_CHIP_Y_OFFSET_PX,
  SELECTED_FILTERS_COLLAPSE_DURATION_MS,
  SELECTED_FILTERS_COLLAPSE_EASING,
  SELECTED_FILTERS_COMPACT_BUTTON_FADE_IN_MS,
  SELECTED_FILTERS_EXPAND_DURATION_MS,
  SELECTED_FILTERS_EXPAND_EASING,
} from '../../constants';
import {
  haloRingBase,
  haloRingHover,
  haloRingPressed,
} from '../../styles/haloRing';
import { getFacetOptionRBKey } from '../../utils/filters';

const DynamicFormattedMessage = FormattedMessage;

const SELECTED_FILTERS_COLLAPSED_HEIGHT = 36;

const SelectedFiltersPanelContainer = styled.div<{ $isExpanded: boolean }>`
  position: relative;
  width: ${({ $isExpanded }) => ($isExpanded ? '100%' : '60vw')};
  /* Expand: width snaps (0ms). Collapse: width transitions. See doc. */
  transition: width
    ${({ $isExpanded }) =>
      $isExpanded ? '0ms' : `${SELECTED_FILTERS_COLLAPSE_DURATION_MS}ms`}
    ${({ $isExpanded }) =>
      $isExpanded ? 'linear' : SELECTED_FILTERS_COLLAPSE_EASING};
`;

const CompactShowMoreWrapper = styled.div<{
  $left: number;
  $top: number;
  $faded: boolean;
  $mounted: boolean;
}>`
  position: absolute;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  transform: translateY(-50%);
  display: ${({ $mounted }) => ($mounted ? 'block' : 'none')};
  opacity: ${({ $faded }) => ($faded ? 0 : 1)};
  pointer-events: ${({ $faded }) => ($faded ? 'none' : 'auto')};
  transition: opacity
    ${({ $faded }) =>
      $faded
        ? `${SELECTED_FILTERS_BUTTON_FADE_OUT_MS}ms`
        : `${SELECTED_FILTERS_COMPACT_BUTTON_FADE_IN_MS}ms`}
    ease;
`;

const SelectedFiltersList = styled.ul<{
  $isExpanded: boolean;
}>`
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  list-style: none;
  padding: 0;
  transition: max-height
    ${({ $isExpanded }) =>
      $isExpanded
        ? `${SELECTED_FILTERS_EXPAND_DURATION_MS}ms`
        : `${SELECTED_FILTERS_COLLAPSE_DURATION_MS}ms`}
    ${({ $isExpanded }) =>
      $isExpanded
        ? SELECTED_FILTERS_EXPAND_EASING
        : SELECTED_FILTERS_COLLAPSE_EASING};
  overflow: hidden;
  margin-top: 8px;
  max-height: ${({ $isExpanded }) =>
    $isExpanded ? '500px' : `${SELECTED_FILTERS_COLLAPSED_HEIGHT}px`};
`;

const chipEnterKeyframes = keyframes`
  from {
    opacity: 0;
    transform: translateY(${SELECTED_FILTERS_CHIP_Y_OFFSET_PX}px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const chipExitKeyframes = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-${SELECTED_FILTERS_CHIP_Y_OFFSET_PX}px);
  }
`;

const InlineShowMoreListItem = styled.li`
  min-height: ${SELECTED_FILTERS_COLLAPSED_HEIGHT}px;
  display: flex;
  align-items: center;
`;

const InlineShowMoreWrapper = styled.div<{
  $phase: 'enter' | null;
  $index: number;
  $faded: boolean;
}>`
  opacity: ${({ $faded }) => ($faded ? 0 : 1)};
  pointer-events: ${({ $faded }) => ($faded ? 'none' : 'auto')};
  transition: opacity ${SELECTED_FILTERS_BUTTON_FADE_OUT_MS}ms ease;

  ${({ $phase, $index }) =>
    $phase === 'enter' &&
    css`
      animation: ${chipEnterKeyframes}
        ${SELECTED_FILTERS_CHIP_ENTER_DURATION_MS}ms
        ${SELECTED_FILTERS_CHIP_ENTER_EASING} both;
      animation-delay: ${SELECTED_FILTERS_CHIP_ENTER_DELAY_MS +
      $index * SELECTED_FILTERS_CHIP_ENTER_STAGGER_MS}ms;
    `}
`;

const ChipListItem = styled.li<{
  $index: number;
  $phase: 'enter' | 'exit' | null;
}>`
  min-height: ${SELECTED_FILTERS_COLLAPSED_HEIGHT}px;
  display: flex;
  align-items: center;
  ${({ $phase, $index }) => {
    if ($phase === 'enter') {
      return css`
        animation: ${chipEnterKeyframes}
          ${SELECTED_FILTERS_CHIP_ENTER_DURATION_MS}ms
          ${SELECTED_FILTERS_CHIP_ENTER_EASING} both;
        animation-delay: ${SELECTED_FILTERS_CHIP_ENTER_DELAY_MS +
        $index * SELECTED_FILTERS_CHIP_ENTER_STAGGER_MS}ms;
      `;
    }
    if ($phase === 'exit') {
      return css`
        animation: ${chipExitKeyframes}
          ${SELECTED_FILTERS_CHIP_EXIT_DURATION_MS}ms ease-in forwards;
      `;
    }
    return '';
  }}
`;

const ShowMoreButton = styled.button`
  height: 28px;
  padding: 0 12px;
  border-radius: 4px;
  display: flex;
  align-items: baseline;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.gridV2ShowMoreBtnBorderHover};
  background-color: transparent;
  cursor: pointer;
  transition:
    border-color 0.2s,
    background-color 0.2s;
  font-size: 14px;
  line-height: 24px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.gridV2ShowMoreBtnText};
  width: max-content;
  white-space: nowrap;
  flex-shrink: 0;
  align-self: flex-start;

  &:hover {
    background-color: ${({ theme }) => theme.colors.gridV2ShowMoreBtnBgHover};
  }

  &:active {
    border-color: ${({ theme }) => theme.colors.gridV2ShowMoreBtnBorderActive};
    background-color: ${({ theme }) => theme.colors.gridV2ShowMoreBtnBgActive};
  }
`;

const Separator = styled.div`
  width: 1px;
  height: 12px;
  background-color: ${({ theme }) => theme.colors.gridV2FilterSeparator};
  border-radius: 64px;
  opacity: 0;
  margin-left: 0;
  align-self: center;
  transition:
    opacity 0.3s ease,
    margin-left 0.3s ease;

  ${ShowMoreButton}:hover & {
    opacity: 1;
    margin-left: 8px;
  }
`;

const ShowMoreButtonText = styled.span`
  font-size: 12px;
  line-height: 1.2;
  font-weight: 400;
`;

const ShowMoreCountWrapper = styled.div`
  display: grid;
  grid-template-columns: 0fr;
  margin-left: 0;
  align-self: center;
  transition:
    grid-template-columns 0.3s ease,
    margin-left 0.3s ease;

  ${ShowMoreButton}:hover & {
    grid-template-columns: 1fr;
    margin-left: 8px;
  }
`;

const ShowMoreCountContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${ShowMoreButton}:hover & {
    opacity: 1;
  }
`;

const SelectedFilterButton = styled.button`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 2px;
  border: none;
  background-color: transparent;
  line-height: 24px;
  font-family: inherit;
  font-size: 14px;
`;

const SelectedFilterButtonRemoveIcon = styled.div`
  position: relative;
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: 64px;
  transition: border-color 0.2s;
  box-sizing: border-box;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.gridV2RemoveBtnIconColor};
  ${haloRingBase};

  svg {
    width: 10px;
    height: 10px;
  }

  &::before {
    border-radius: inherit;
  }

  &:hover {
    border-color: transparent;
  }

  &:active {
    border-color: transparent;
  }

  &:hover::before {
    ${haloRingHover};
  }

  &:active::before {
    ${haloRingPressed};
  }
`;

interface SelectedFiltersPanelProps {
  selectedFacets: Array<{
    facetName: string;
    urlName: string;
    facetOption: string;
    facetOptionDisplayName: string;
  }>;
  handleOnChange: (params: { facetName: string; facetOption: string }) => void;
}

const SelectedFiltersPanel: React.FC<SelectedFiltersPanelProps> = ({
  selectedFacets,
  handleOnChange,
}) => {
  const [isSelectedFiltersExpanded, setIsSelectedFiltersExpanded] =
    useState(false);
  const [hiddenFiltersCount, setHiddenFiltersCount] = useState(0);
  const [compactButtonLeft, setCompactButtonLeft] = useState(0);
  const [compactButtonTop, setCompactButtonTop] = useState(
    SELECTED_FILTERS_COLLAPSED_HEIGHT / 2
  );
  const [compactFaded, setCompactFaded] = useState(false);
  const [compactMounted, setCompactMounted] = useState(true);
  const [chipPhase, setChipPhase] = useState<'enter' | 'exit' | null>(null);
  const [exitVisibleSnapshot, setExitVisibleSnapshot] = useState(0);
  const [collapseMaxHeight, setCollapseMaxHeight] = useState<number | null>(
    null
  );
  const [inlineMounted, setInlineMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedFiltersRef = useRef<HTMLUListElement>(null);

  // Derived: the toggle button is needed iff any chips would be hidden in
  // the compact layout. `checkOverflow` only updates while compact, so this
  // value is stable while expanded.
  const hasSelectedFiltersOverflow = hiddenFiltersCount > 0;

  // Signature that changes on any chip add/remove/swap (length alone misses
  // swaps). `facetName:facetOption` avoids collisions when the same option
  // value appears under different facets. Drives the layout effect's
  // re-measure trigger.
  const selectedFacetsKey = selectedFacets
    .map((f) => `${f.facetName}:${f.facetOption}`)
    .join('|');

  // Ref-mirror of isSelectedFiltersExpanded so `checkOverflow` can gate on
  // it without listing it as a dep. See `SelectedFiltersPanel.md` →
  // "Measurement".
  const isExpandedRef = useRef(isSelectedFiltersExpanded);
  useLayoutEffect(() => {
    isExpandedRef.current = isSelectedFiltersExpanded;
  }, [isSelectedFiltersExpanded]);

  const checkOverflow = useCallback(() => {
    // Skip while expanded — a 100%-wide container fits more chips per row
    // than compact, so any count measured here would disagree with the
    // compact Show More count.
    if (isExpandedRef.current) {
      return;
    }
    if (
      !selectedFiltersRef.current ||
      !containerRef.current ||
      selectedFacetsKey === ''
    ) {
      setHiddenFiltersCount(0);
      return;
    }
    const element = selectedFiltersRef.current;
    const containerRect = containerRef.current.getBoundingClientRect();
    const listRect = element.getBoundingClientRect();
    const hasOverflow =
      element.scrollHeight > SELECTED_FILTERS_COLLAPSED_HEIGHT;

    if (hasOverflow) {
      const listItems = element.querySelectorAll('li[data-chip]');
      let hiddenCount = 0;
      let lastVisibleRect: DOMRect | null = null;
      listItems.forEach((item) => {
        const rect = item.getBoundingClientRect();
        if (rect.top >= listRect.top + SELECTED_FILTERS_COLLAPSED_HEIGHT) {
          hiddenCount++;
        } else {
          lastVisibleRect = rect;
        }
      });
      setHiddenFiltersCount(hiddenCount);
      if (lastVisibleRect) {
        const r: DOMRect = lastVisibleRect;
        setCompactButtonLeft(r.right - containerRect.left + 16);
        setCompactButtonTop(r.top - containerRect.top + r.height / 2);
      }
    } else {
      setHiddenFiltersCount(0);
    }
  }, [selectedFacetsKey]);

  // Re-measure on chip set changes. useLayoutEffect runs post-commit,
  // pre-paint so the measurement reflects the new DOM. `checkOverflow`
  // only regenerates when `selectedFacetsKey` changes, so this effect
  // doesn't fire on expand/collapse (the ResizeObserver below handles
  // those).
  useLayoutEffect(() => {
    checkOverflow();
  }, [checkOverflow]);

  // Expand-side orchestration. The collapse side is imperative —
  // see `handleToggleExpanded` below.
  useEffect(() => {
    if (!isSelectedFiltersExpanded) {
      return undefined;
    }
    setCompactFaded(true);
    setChipPhase('enter');
    setInlineMounted(true);
    const compactUnmountTimer = setTimeout(
      () => setCompactMounted(false),
      SELECTED_FILTERS_BUTTON_FADE_OUT_MS
    );
    return () => clearTimeout(compactUnmountTimer);
  }, [isSelectedFiltersExpanded]);

  // End-of-collapse handler — see `SelectedFiltersPanel.md` → "Animation
  // timeline — collapse" for why each step happens at this point.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }
    const handleTransitionEnd = (e: TransitionEvent) => {
      if (
        e.target === container &&
        e.propertyName === 'width' &&
        !isSelectedFiltersExpanded
      ) {
        setInlineMounted(false);
        checkOverflow();
        setChipPhase(null);
        setCompactMounted(true);
        requestAnimationFrame(() => setCompactFaded(false));
      }
    };
    container.addEventListener('transitionend', handleTransitionEnd);
    return () =>
      container.removeEventListener('transitionend', handleTransitionEnd);
  }, [checkOverflow, isSelectedFiltersExpanded]);

  // Primary re-measure trigger. Fires on viewport resize and throughout
  // the collapse width transition; `checkOverflow` early-returns during
  // expand. rAF-throttled so a 220ms transition produces at most one
  // measurement per frame instead of many (see `SelectedFiltersPanel.md`
  // → "Measurement").
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    let rafId: number | null = null;
    const observer = new ResizeObserver(() => {
      if (rafId !== null) {
        return;
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        checkOverflow();
      });
    });
    observer.observe(container);
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      observer.disconnect();
    };
  }, [checkOverflow]);

  if (selectedFacets?.length === 0) {
    return null;
  }

  // Collapse is imperative so we can sequence: fade chips → snap
  // max-height → flip layout state. See `SelectedFiltersPanel.md` →
  // "Animation timeline — collapse".
  const handleToggleExpanded = () => {
    if (isSelectedFiltersExpanded) {
      // Freeze the exit-animation set so mid-collapse re-measures can't
      // drop a chip out of it and snap it back to opacity 1.
      setExitVisibleSnapshot(
        Math.max(selectedFacets.length - hiddenFiltersCount, 0)
      );
      // Pin max-height to current content height so the subsequent
      // transition to 36px is a monotonic decrease (prevents height bounce
      // from chip reflow while width is shrinking).
      if (selectedFiltersRef.current) {
        setCollapseMaxHeight(selectedFiltersRef.current.scrollHeight);
      }
      setChipPhase('exit');
      setTimeout(() => {
        setCollapseMaxHeight(null);
        setIsSelectedFiltersExpanded(false);
      }, SELECTED_FILTERS_CHIP_EXIT_DURATION_MS);
    } else {
      setIsSelectedFiltersExpanded(true);
    }
  };

  const toggleButton = hasSelectedFiltersOverflow ? (
    <ShowMoreButton type="button" onClick={handleToggleExpanded}>
      {isSelectedFiltersExpanded ? (
        <FormattedMessage
          id="grid_static.show_less"
          defaultMessage="Show Less"
        />
      ) : (
        <FormattedMessage
          id="grid_static.show_more"
          defaultMessage="Show More"
        />
      )}
      <Separator />
      <ShowMoreCountWrapper>
        <ShowMoreCountContainer>
          {isSelectedFiltersExpanded ? (
            <HideIcon color="currentColor" />
          ) : (
            <PlusIcon2 color="currentColor" />
          )}
          <ShowMoreButtonText>{hiddenFiltersCount}</ShowMoreButtonText>
        </ShowMoreCountContainer>
      </ShowMoreCountWrapper>
    </ShowMoreButton>
  ) : null;

  return (
    <SelectedFiltersPanelContainer
      ref={containerRef}
      $isExpanded={isSelectedFiltersExpanded}
    >
      <SelectedFiltersList
        ref={selectedFiltersRef}
        $isExpanded={isSelectedFiltersExpanded}
        data-autotag="filtered-by-textbox"
        style={
          collapseMaxHeight !== null
            ? {
                maxHeight: `${collapseMaxHeight}px`,
                transition: 'none',
              }
            : undefined
        }
      >
        {selectedFacets.map(
          ({ facetName, facetOption, facetOptionDisplayName }, i) => {
            const visibleInCompact =
              chipPhase === 'exit'
                ? exitVisibleSnapshot
                : Math.max(selectedFacets.length - hiddenFiltersCount, 0);
            const isAnimated = i >= visibleInCompact;
            const relativeIndex = Math.max(i - visibleInCompact, 0);
            return (
              <ChipListItem
                key={facetOption}
                data-chip
                $index={relativeIndex}
                $phase={isAnimated ? chipPhase : null}
              >
                <SelectedFilterButton
                  type="button"
                  data-autotag="filtered-by-option"
                >
                  <DynamicFormattedMessage
                    id={`grid_dynamic.${getFacetOptionRBKey(
                      facetName,
                      facetOption
                    )}`}
                    defaultMessage={facetOptionDisplayName}
                  />
                  <SelectedFilterButtonRemoveIcon
                    data-autotag="filtered-by-option-close-icon"
                    onClick={() => handleOnChange({ facetName, facetOption })}
                  >
                    <CloseIcon2 color="currentColor" />
                  </SelectedFilterButtonRemoveIcon>
                </SelectedFilterButton>
              </ChipListItem>
            );
          }
        )}
        {inlineMounted && toggleButton && (
          <InlineShowMoreListItem>
            <InlineShowMoreWrapper
              $phase={chipPhase === 'enter' ? 'enter' : null}
              $index={hiddenFiltersCount}
              $faded={chipPhase !== 'enter'}
            >
              {toggleButton}
            </InlineShowMoreWrapper>
          </InlineShowMoreListItem>
        )}
      </SelectedFiltersList>
      {toggleButton && (
        <CompactShowMoreWrapper
          $left={compactButtonLeft}
          $top={compactButtonTop}
          $faded={compactFaded}
          $mounted={compactMounted}
        >
          {toggleButton}
        </CompactShowMoreWrapper>
      )}
    </SelectedFiltersPanelContainer>
  );
};

export default SelectedFiltersPanel;
